import { evt_AppReady } from '../react/constants/custom-event-names';
import { getAllObjects } from '../v3d-utils/custom-functions';

declare type MaterialCollections = {
  [key: string]: Array<Verge.Material>;
};

declare type ObjectMap = {
  [key: string]: Verge.Object;
};

declare type OpaqueResult = {
  [key: string]: boolean;
};

const DUMMY_SCALE_FACTOR = -1000; // Project-dependent value
const DUMMY_SUFFIX = '_Opacity';
const TYPE_MESH = 'Mesh';
const TYPE_GROUP = 'Group';
const EMPTY = '';

const sceneObjects: ObjectMap = {};
const opaqueMap: OpaqueResult = {};
const origMaterialsPerObjectMap: MaterialCollections = {}; // Don't manipulate these, because we do not create copies!
const blendMaterialsPerObjectMap: MaterialCollections = {};
const observedObjectNames: Array<string> = [];
const helpersMap: ObjectMap = {};

const addMaterialToAnimationGroup = (
  material: Verge.Material,
  groupName: string,
  map: MaterialCollections
) => {
  if (map[groupName]) {
    map[groupName].push(material);
  } else {
    map[groupName] = [material];
  }
};

const materialChangeRequired = (name: string, alpha: number) => {
  const lastState = opaqueMap[name];
  if (alpha === 1 && lastState === false) {
    opaqueMap[name] = true;
    return true;
  } else if (alpha !== 1 && lastState === true) {
    opaqueMap[name] = false;
    return true;
  }
  return false;
};

const switchMaterialsFromObject = (
  found: Verge.Object,
  materials: MaterialCollections
) => {
  const { type, name } = found;
  if (type === TYPE_MESH) {
    // eslint-disable-next-line prefer-destructuring
    found.material = materials[name][0];
    found.material.needsUpdate = true;
  } else if (type === TYPE_GROUP) {
    found.children.forEach((subChild: Verge.Object, index: number) => {
      if (subChild.type === TYPE_MESH) {
        subChild.material = materials[name][index];
        subChild.material.needsUpdate = true;
      }
    });
  }
};

const evaluateOpacity = (name: string) => {
  const alpha = helpersMap[name].position.z * DUMMY_SCALE_FACTOR;

  // Handle floating point inaccuracy
  if (alpha < 0.05) {
    return 0;
  } else if (alpha > 0.95) {
    return 1;
  }

  return alpha;
};

export const updateMaterialOpacity = () => {
  observedObjectNames.forEach((name) => {
    const alpha = evaluateOpacity(name);

    blendMaterialsPerObjectMap[name].forEach((material: Verge.Material) => {
      material.opacity = alpha;
      material.visible = material.opacity !== 0;
      material.needsUpdate = true;
    });

    if (materialChangeRequired(name, alpha)) {
      const found = sceneObjects[name];
      if (!found) {
        return;
      }
      if (opaqueMap[name]) {
        switchMaterialsFromObject(found, origMaterialsPerObjectMap);
      } else {
        switchMaterialsFromObject(found, blendMaterialsPerObjectMap);
      }
    }
  });
};

// https://www.soft8soft.com/docs/api/en/materials/MeshNodeMaterial.html

const createBlendMaterial = (child: Verge.Object, helperName: string) => {
  const { material } = child;
  const { x, y, z } = material.nodeRGB[0];

  const clone = new window.THREE.MeshPhongMaterial({
    transparent: true,
    color: new window.THREE.Color(x, y, z),
  });

  addMaterialToAnimationGroup(material, helperName, origMaterialsPerObjectMap);
  addMaterialToAnimationGroup(clone, helperName, blendMaterialsPerObjectMap);
};

window.addEventListener(evt_AppReady, () => {
  const children = getAllObjects();

  // Create array of dummy objects
  const animatedHelpers = children.filter((child: Verge.Object) => {
    return child.name && child.name.endsWith(DUMMY_SUFFIX);
  });

  // Fill array of 3d models, etc.
  children
    .filter((child: Verge.Object) => !animatedHelpers.includes(child))
    .forEach((child: Verge.Object) => (sceneObjects[child.name] = child));

  // Fill array of helpers, named after some connected 3D models
  animatedHelpers.forEach((helper: Verge.Object) => {
    const name = helper.name.replace(DUMMY_SUFFIX, EMPTY);
    if (!sceneObjects[name]) {
      console.warn(`Could not find object '${name}' for material animation`);
      return;
    }
    helpersMap[name] = helper;
  });

  // Create enough materials for each animated 3D model
  Object.keys(helpersMap).forEach((name: string) => {
    const found = sceneObjects[name];
    const { type } = found;
    if (type === TYPE_MESH) {
      opaqueMap[name] = true;
      // 3d model consists of 1 mesh instance (=material)
      createBlendMaterial(found, name);
    } else if (type === TYPE_GROUP) {
      opaqueMap[name] = true;
      // 3d model consists of multiple mesh instances (=material)
      found.children.forEach((subChild: Verge.Object) => {
        if (subChild.type === TYPE_MESH) {
          createBlendMaterial(subChild, name);
        }
      });
    }
  });

  // Remember names of animated 3D models
  observedObjectNames.push(...Object.keys(blendMaterialsPerObjectMap));

  updateMaterialOpacity();
});
